import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ProfileContext from '../context/ProfileContext';
import {
    Layout, Menu, theme, Button, Drawer, Avatar, Popover, Spin, Typography, Tag,
    Divider, AutoComplete, Input, Space
} from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    LogoutOutlined,
    BookOutlined,
    TeamOutlined,
    SettingOutlined,
    SolutionOutlined,
    FileTextOutlined,
    ReadOutlined,
    CalendarOutlined,
    MenuOutlined,
    HistoryOutlined,
    InboxOutlined,
    DashOutlined,
    SearchOutlined,
    HomeOutlined, // Added HomeOutlined for Dashboard bottom icon
    BarChartOutlined, // Added for Reports icon
    MessageOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageLogger from '../hooks/usePageLogger';
import api, { imageBaseUrl } from '../services/api';
import { getAllClasses } from '../services/classService';
import { globalSearch } from '../services/searchService';
import { useNotification } from '../context/NotificationContext';
import { getTutorGroup } from '../services/assignmentService';
import { PageHeaderProvider, usePageHeader } from '../context/PageHeaderContext';
// Import CSS to handle the bottom navigation bar styling
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// Check interval in milliseconds (e.g., 5 minutes)
const ROLE_SYNC_INTERVAL = 5 * 60 * 1000;

// --- NEW COMPONENT: Mobile Bottom Navigation ---
const MobileBottomNav = React.memo(({ menuItems, location, navigate, closeMobileDrawer }) => {

    // Manually define the structure for the 5 core navigation links
    const getCoreNavData = (allMenuItems) => {
        // Build a map of all leaf menu items (only actual routes, not submenus)
        const pathMap = {};

        const extractLeafItems = (items) => {
            items.forEach(item => {
                if (item.children) {
                    // This is a submenu, recurse into children
                    extractLeafItems(item.children);
                } else {
                    // This is a leaf item (actual route)
                    let label = item.key === '/' ? 'Home' : null;
                    if (!label && item.label && item.label.props) {
                        label = item.label.props.children;
                    }
                    if (label) {
                        pathMap[item.key] = { key: item.key, icon: item.icon, label };
                    }
                }
            });
        };

        extractLeafItems(allMenuItems);

        // Define the fixed order and fallbacks
        const corePaths = [
            { key: '/', icon: <HomeOutlined />, label: 'Home' }, // Using HomeOutlined now
            { key: '/students', icon: <TeamOutlined />, label: 'Students' },
            { key: '/attendance', icon: <CheckCircleOutlined />, label: 'Attendance' },
            { key: '/marks', icon: <BookOutlined />, label: 'Marks' },
            { key: '/profile', icon: <UserOutlined />, label: 'Profile' },
        ];

        // Use the defined core paths, falling back to the original menu structure only for icons/labels
        return corePaths
            .map(core => ({
                ...core,
                icon: pathMap[core.key]?.icon || core.icon, // Use mapped icon if available
                label: pathMap[core.key]?.label || core.label, // Use mapped label if available
            }))
            .filter(item => item.key === '/' || pathMap[item.key]); // Ensure only available routes are shown (except '/')
    };

    const navItems = useMemo(() => getCoreNavData(menuItems), [menuItems]);

    // Function to get the current active key for highlighting (CRITICAL FIX)
    const getActiveKey = () => {
        const currentPath = location.pathname;

        // 1. Exact match for Home ('/')
        if (currentPath === '/') return '/';

        // 2. Find partial matches
        const activeItem = navItems.find(item =>
            item.key !== '/' && currentPath.startsWith(item.key)
        );

        return activeItem?.key || '/';
    };

    const activeKey = getActiveKey();

    return (
        <div className="mobile-bottom-nav">
            {navItems.map(item => (
                // Use a simple div for the click target
                <div
                    key={item.key}
                    className={`nav-item ${activeKey === item.key ? 'active' : ''}`}
                    onClick={() => {
                        navigate(item.key); // Use navigate directly
                        closeMobileDrawer();
                    }}
                >
                    {item.icon}
                    <Text className="nav-label">{item.label}</Text>
                </div>
            ))}
        </div>
    );
});


const MainLayout = ({ children }) => {
    const { token } = theme.useToken();
    const { logout, user, syncRole } = useAuth();
    const { message: notify } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    usePageLogger();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [teacherClass, setTeacherClass] = useState(null);
    const [userTutorGroup, setUserTutorGroup] = useState([]);

    // Fetch school settings
    const [schoolSettings, setSchoolSettings] = useState(null);

    useEffect(() => {
        const fetchSchoolSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSchoolSettings(response.data);
            } catch (error) {
                console.error('Failed to fetch school settings:', error);
            }
        };
        fetchSchoolSettings();
    }, []);

    const schoolName = schoolSettings?.school_name || 'School Management System';
    const schoolLogo = schoolSettings?.logo_url ? `${imageBaseUrl}${schoolSettings.logo_url}` : require('../assets/logo.png');

    const [hasSubjectAssignments, setHasSubjectAssignments] = useState(false);

    useEffect(() => {
        const fetchTeacherAssignments = async () => {
            if (user && user.role === 'teacher') {
                try {
                    const response = await api.get(`/assignments/teacher/${user.id}/subjects`);
                    setHasSubjectAssignments(response.data.length > 0);
                } catch (err) {
                    setHasSubjectAssignments(false);
                }
            } else {
                setHasSubjectAssignments(false);
            }
        };
        fetchTeacherAssignments();
    }, [user]);


    // Search State
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);



    // --- Profile Picture and URL Logic ---
    const profilePictureFullUrl = useMemo(() => {
        if (!profileData?.profile_picture_url) return null;
        const cacheBuster = Date.now();
        return `${imageBaseUrl}${profileData.profile_picture_url}?cb=${cacheBuster}`;
    }, [profileData]);

    const refreshProfileData = useCallback(async () => {
        if (!user) return;
        setProfileLoading(true);
        try {
            const response = await api.get('/profile/me');
            setProfileData(response.data);
        } catch (error) {
            console.error('Failed to fetch user profile for layout header:', error);
        } finally {
            setProfileLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refreshProfileData();
        if (user && user.role === 'teacher') {
            getTutorGroup(user.id)
                .then(studentIds => setUserTutorGroup(studentIds))
                .catch(err => {
                    console.error("Fetch Tutor Group Error:", err.message);
                    setUserTutorGroup([]);
                });
        }
    }, [user, refreshProfileData]);

    // Fetch teacher's class if user is a teacher
    useEffect(() => {
        const fetchTeacherClass = async () => {
            if (user && user.role === 'teacher') {
                try {
                    const classes = await getAllClasses();
                    const myClass = classes.find(c => c.class_teacher_id === user.id);
                    setTeacherClass(myClass ? myClass.name : null);
                } catch (err) {
                    setTeacherClass(null);
                }
            } else {
                setTeacherClass(null);
            }
        };
        fetchTeacherClass();
    }, [user]);


    // Responsive handling: track mobile state and adjust drawer visibility
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setMobileDrawerVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Role Synchronization Timer
    const executeSyncCheck = useCallback(async () => {
        const roleChanged = await syncRole();
        if (roleChanged) {
            notify.error('Your session has been terminated due to an administrator changing your permissions. Please log in again.', 10);
        }
    }, [syncRole, notify]);


    useEffect(() => {
        if (user) {
            executeSyncCheck();
            const intervalId = setInterval(executeSyncCheck, ROLE_SYNC_INTERVAL);
            return () => clearInterval(intervalId);
        }
    }, [user, executeSyncCheck]);


    // User / auth handlers
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const toggleMobileDrawer = () => {
        setMobileDrawerVisible(!mobileDrawerVisible);
    };

    const closeMobileDrawer = useCallback(() => {
        setMobileDrawerVisible(false);
    }, []);

    // Global Search Handler
    const handleSearch = useCallback(async (value) => {
        if (value.length < 3) {
            setSearchOptions([]);
            return;
        }
        setSearchLoading(true);
        const results = await globalSearch(value);

        const options = results.map(item => ({
            value: item.title,
            label: (
                <Link to={item.link} onClick={() => setSearchOptions([])}>
                    <Space>
                        {item.type === 'student' && <TeamOutlined style={{ color: token.colorPrimary }} />}
                        {item.type === 'user' && <UserOutlined style={{ color: '#1890ff' }} />}
                        {item.type === 'class' && <SettingOutlined style={{ color: '#faad14' }} />}
                        <div>
                            <Text strong>{item.title}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
                        </div>
                    </Space>
                </Link>
            ),
        }));
        setSearchOptions(options);
        setSearchLoading(false);
    }, []);

    // Menu generation helper:
    const getMenuItems = useCallback((user, closeMobileDrawer) => {
        if (!user) return [];
        const isTutor = user.role === 'teacher' && userTutorGroup.length > 0;

        // Helper function for creating a Menu item with the Link component
        const createMenuItem = (key, icon, label) => ({
            key, icon, label: <Link to={key} onClick={closeMobileDrawer}>{label}</Link>
        });

        // Helper function for creating a submenu
        const createSubMenu = (key, icon, label, children) => ({
            key, icon, label, children
        });

        const baseItems = [
            createMenuItem('/', <DashboardOutlined />, 'Dashboard'),
        ];

        // Submenus for Admin/Principal
        const academicManagementSubmenu = createSubMenu('academic-mgmt', <BookOutlined />, 'Academic Management', [
            createMenuItem('/classes', <TeamOutlined />, 'Classes'),
            createMenuItem('/subjects', <ReadOutlined />, 'Subjects'),
            createMenuItem('/class-subjects', <ReadOutlined />, 'Class Subjects'),
            createMenuItem('/academic-years', <HistoryOutlined />, 'Academic Years'),
        ]);

        const studentManagementSubmenu = createSubMenu('student-mgmt', <TeamOutlined />, 'Student Management', [
            createMenuItem('/students', <TeamOutlined />, 'Students'),
            createMenuItem('/attendance', <CheckCircleOutlined />, 'Attendance'),
            createMenuItem('/marks', <BookOutlined />, 'Marks'),
        ]);

        const admissionManagementSubmenu = createSubMenu('admission-mgmt', <InboxOutlined />, 'Admission Management', [
            createMenuItem('/inquiries', <InboxOutlined />, 'Pending Applications'),
            createMenuItem('/inquiries/rejected', <CloseOutlined />, 'Rejected Applications'),
        ]);

        const administrationSubmenu = createSubMenu('admin', <SettingOutlined />, 'Administration', [
            createMenuItem('/users', <SettingOutlined />, 'User Management'),
            createMenuItem('/assignments', <SolutionOutlined />, 'Assignments'),
            createMenuItem('/calendar', <CalendarOutlined />, 'Calendar'),
            createMenuItem('/exams', <FileTextOutlined />, 'Exams'),
            createMenuItem('/reports', <BarChartOutlined />, 'Reports'),
            createMenuItem('/feedback-dashboard', <MessageOutlined />, 'Tutor Feedback Monitor'),
            createMenuItem('/settings', <SettingOutlined />, 'Settings'),
        ]);

        const auditSubmenu = createSubMenu('audit', <DashOutlined />, 'Audit', [
            createMenuItem('/logs', <DashOutlined />, 'Audit Logs'),
        ]);

        const isClassOrSubjectTeacher = !!(teacherClass || hasSubjectAssignments);

        // Teacher submenus items based on toggles
        const teacherStudentManagementSubmenuItems = [];
        if (schoolSettings?.allow_teacher_student_view !== false || isClassOrSubjectTeacher) {
            teacherStudentManagementSubmenuItems.push(createMenuItem('/students', <TeamOutlined />, 'Students'));
        }
        if (schoolSettings?.allow_teacher_attendance_entry !== false || isClassOrSubjectTeacher) {
            teacherStudentManagementSubmenuItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, 'Attendance'));
        }
        if (schoolSettings?.allow_teacher_mark_view !== false || isClassOrSubjectTeacher) {
            teacherStudentManagementSubmenuItems.push(createMenuItem('/marks', <BookOutlined />, 'Marks'));
        }
        teacherStudentManagementSubmenuItems.push(createMenuItem('/calendar', <CalendarOutlined />, 'Calendar'));
        teacherStudentManagementSubmenuItems.push(createMenuItem('/reports', <BarChartOutlined />, 'Reports'));

        const teacherStudentManagementSubmenu = createSubMenu('student-mgmt', <TeamOutlined />, 'Student Management', teacherStudentManagementSubmenuItems);

        if (user.role === 'super_admin') {
            baseItems.push(academicManagementSubmenu);
            baseItems.push(studentManagementSubmenu);
            baseItems.push(admissionManagementSubmenu);
            baseItems.push(administrationSubmenu);
            baseItems.push(auditSubmenu);
            return baseItems;
        }

        if (user.role === 'principal') {
            baseItems.push(academicManagementSubmenu);
            baseItems.push(studentManagementSubmenu);
            baseItems.push(admissionManagementSubmenu);
            baseItems.push(administrationSubmenu);
            // Only developer sees logs (if not using the super_admin role)
            if (user.username === "developer") {
                baseItems.push(auditSubmenu);
            }
            return baseItems;
        }

        if (isTutor) {
            baseItems.push(createMenuItem('/students', <TeamOutlined />, 'My Students'));
        }

        if (user.role === 'teacher') {
            baseItems.push(teacherStudentManagementSubmenu);
            return baseItems;
        }

        return baseItems;
    }, [userTutorGroup, teacherClass, schoolSettings, hasSubjectAssignments]); // Added dependencies

    // Memoize menu items
    const menuItems = useMemo(() => getMenuItems(user, closeMobileDrawer), [user, closeMobileDrawer, getMenuItems]);

    // Memoize the Menu component
    const menuComponent = useMemo(() => (
        <Menu
            theme="light"
            selectedKeys={[location.pathname]}
            mode="inline"
            items={menuItems}
            style={{ borderRight: 0, fontSize: 16 }}
        />
    ), [menuItems, location.pathname]);


    // Content for the Popover (User Info / Logout)
    const popoverContent = (
        <div style={{ padding: '16px' }}>
            <p style={{ margin: '0 0 8px 0' }}>Logged in as: <Text strong>{profileData?.full_name || user?.username}</Text></p>
            <p style={{ margin: '0 0 8px 0' }}>Role: <Tag color="blue">{user?.role.toUpperCase()}</Tag></p>
            <Divider style={{ margin: '8px 0' }} />
            <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => { closeMobileDrawer(); navigate('/profile'); }}
                style={{ paddingLeft: 0 }}
            >
                My Profile
            </Button>
            <Button
                type="default"
                icon={<LogoutOutlined />}
                onClick={() => {
                    if (window.confirm('Are you sure you want to logout?')) {
                        handleLogout();
                    }
                }}
                danger
                style={{ marginTop: 8 }}
            >
                Logout
            </Button>
        </div>
    );

    // Determine content margin based on mobile/desktop and bottom nav presence
    const contentPaddingBottom = isMobile ? 80 : 24;
    const contentMarginLeft = isMobile ? 0 : (collapsed ? 80 : 200);

    // --- NEW: Dynamic Mobile Title Logic ---
    const { title: pageTitle } = usePageHeader();

    return (
        <ProfileContext.Provider value={{ profileData, profileLoading, refreshProfileData, setProfileData }}>
            <Layout style={{ minHeight: '100vh' }}>

                {/* Desktop Sidebar */}
                {!isMobile && (
                    <Sider
                        theme='light'
                        collapsible
                        collapsed={collapsed}
                        onCollapse={(value) => setCollapsed(value)}
                        // breakpoint='xxl'
                        style={{ overflow: 'auto', height: '100vh', position: 'fixed', left: 0 }}
                    >
                        {/* Logo and Menu... */}
                        <div className="logo" style={{ margin: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                src={schoolLogo}
                                alt="School Logo"
                                style={{ height: collapsed ? '40px' : '64px', transition: 'height 0.2s', objectFit: 'contain' }}
                            />
                        </div>
                        {menuComponent}
                    </Sider>
                )}

                {/* Mobile Drawer (used for the secondary menu items) */}
                <Drawer
                    title="All Menus"
                    placement="left"
                    onClose={closeMobileDrawer}
                    open={mobileDrawerVisible}
                    width={300}
                    aria-label="Main navigation"
                >
                    {menuComponent}
                </Drawer>

                {/* Main content area */}
                <Layout style={{ marginLeft: contentMarginLeft, transition: 'margin-left 0.2s' }}>

                    {/* Header (Simplified on Mobile) */}
                    <Header style={{
                        padding: isMobile ? '0 12px' : '0 24px',
                        background: token.colorBgContainer,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100, // Increased z-index
                    }}>
                        {/* Title/Menu Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <h2 style={{
                                margin: 0,
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 800,
                                color: '#1E293B',
                                letterSpacing: '-0.02em',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {isMobile ? (pageTitle || schoolName) : schoolName}
                            </h2>
                        </div>

                        {/* Search Input (Hidden on super small screens, visible on desktop) */}
                        {!isMobile && (
                            <div style={{ flexGrow: 1, maxWidth: 400, margin: '0 20px' }}>
                                <AutoComplete
                                    options={searchOptions}
                                    onSearch={handleSearch}
                                    style={{ width: '100%' }}
                                    filterOption={false}
                                    notFoundContent={searchLoading ? <Spin size="small" /> : 'No results found.'}
                                    dropdownMatchSelectWidth={300}
                                >
                                    <Input placeholder="Search students, users, or classes..." prefix={<SearchOutlined />} allowClear />
                                </AutoComplete>
                            </div>
                        )}

                        {/* Profile/Logout (right side) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {localStorage.getItem('adminToken') && (
                                <Tag color="volcano" style={{ fontSize: isMobile ? 12 : 14, fontWeight: 'bold' }}>
                                    Impersonating: {profileData?.full_name || user?.username} | {' '}
                                    <span 
                                        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }}
                                        onClick={() => {
                                            const adminToken = localStorage.getItem('adminToken');
                                            localStorage.setItem('token', adminToken);
                                            localStorage.removeItem('adminToken');
                                            window.location.href = '/users';
                                        }}
                                    >
                                        Stop
                                    </span>
                                </Tag>
                            )}

                            {teacherClass && (
                                <Tag color="green" style={{ fontSize: isMobile ? 12 : 14 }}>
                                    {teacherClass}
                                </Tag>
                            )}

                            <Spin spinning={profileLoading} size="small">
                                <Popover
                                    placement="bottomRight"
                                    content={popoverContent}
                                    trigger="click"
                                    overlayInnerStyle={{ padding: 0 }}
                                >
                                    <Avatar
                                        icon={<UserOutlined />}
                                        size={isMobile ? 36 : 40}
                                        src={profilePictureFullUrl}
                                        alt={profileData?.full_name || user?.username}
                                        style={{ cursor: 'pointer', backgroundColor: token.colorPrimary, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    />
                                </Popover>
                            </Spin>

                            {/* Mobile menu button (for secondary menu items) */}
                            {isMobile && (
                                <Button
                                    type="text"
                                    icon={<MenuOutlined />}
                                    onClick={toggleMobileDrawer}
                                    style={{ fontSize: '18px' }}
                                />
                            )}
                        </div>
                    </Header>

                    {/* Content (adjusted padding) */}
                    <Content style={{
                        margin: isMobile ? '12px 8px 0' : '24px 16px',
                        overflow: 'initial'
                    }}>
                        <div style={{
                            padding: isMobile ? 12 : 24,
                            paddingBottom: contentPaddingBottom, // Dynamic bottom padding
                            minHeight: 360,
                            background: token.colorBgContainer,
                            borderRadius: isMobile ? 8 : token.borderRadiusLG
                        }}>
                            {children}
                        </div>
                    </Content>
                </Layout>

                {/* --- NEW: Fixed Bottom Navigation for Mobile --- */}
                {isMobile && (
                    <MobileBottomNav
                        menuItems={menuItems}
                        location={location}
                        navigate={navigate}
                        closeMobileDrawer={closeMobileDrawer}
                    />
                )}
            </Layout>
        </ProfileContext.Provider>
    );
};

const MainLayoutWrapper = (props) => (
    <PageHeaderProvider>
        <MainLayout {...props} />
    </PageHeaderProvider>
);

export default MainLayoutWrapper;